import React from 'react';
import MeetingContactAvatar from 'flow/components/meeting/shared/MeetingContactAvatar';
import { MeetingMember } from 'flow/types/Meeting';
import { TypedMap } from 'flow/types/TypedMap';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';

interface Props {
    member: TypedMap<MeetingMember>;
    widthPercentage: number;
}

/**
 * One participant in the minimized meeting bar. Split out of
 * MeetingMinimizedMembers, which rendered these rows from a plain function and
 * so could hold no hooks of its own — leaving every row here without a contact,
 * and every colleague without their picture.
 *
 * Draws the avatar directly rather than through MeetingMemberAvatar: this row
 * never carries video, and that component's wrapper is built to fill a tile.
 */
const MeetingMinimizedMember = ({ member, widthPercentage }: Props) => {
    const nameWithUUID = member.get('displayName');
    const number = member.get('number');

    return (
        <div
            className="me-3 flex h-12 items-center justify-start rounded-md bg-gray-900 ps-3"
            style={{ width: `${widthPercentage}%` }}
        >
            {/* shrink-0: a long name must push out of the row, not squash
                the avatar. */}
            <div className="shrink-0">
                <MeetingContactAvatar
                    chatUserKey={member.getIn(['identity', 'chatUserKey'])}
                    phoneNumber={number}
                    displayName={nameWithUUID}
                    size="medium"
                />
            </div>
            <div className="p-3 text-base">
                {sliceUUIDFromUserName(nameWithUUID)}
            </div>
        </div>
    );
};

// Memoized for the same reason as the participant rows: the bar re-renders
// around it, and a row's own member rarely changes.
export default React.memo(MeetingMinimizedMember);
